import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";

import {
  ButlerV1HardwareAdapterConfigurationDto,
  HardwareAdapterConfigurationDto,
  HardwareAdapterDirection,
  HardwareAdapterOutputIndex,
  HardwareAdapterOperation,
  HardwareAdapterValidity,
  OpaqueHardwareConfigurationPayload,
} from "../../../../models/api/hardwareAdapterConfigurationDto";
import { HARDWARE_ADAPTER_TYPES, HardwareAdapterType } from "../../../../enums/hardwareAdapterTypes";

export interface HardwareConfigurationOption<T> {
  label: string;
  value: T;
  disabled?: boolean;
}

export type HardwareConfigurationValidity = HardwareAdapterValidity | { status: "pending" };

export interface ButlerV1ConfigurationFormControls {
  operation: FormControl<HardwareAdapterOperation | null>;
  direction: FormControl<HardwareAdapterDirection | null>;
  index: FormControl<HardwareAdapterOutputIndex | null>;
  label: FormControl<string>;
  signalId: FormControl<string | null>;
}

export type ButlerV1ConfigurationForm = FormGroup<ButlerV1ConfigurationFormControls>;

export interface SupportedHardwareConfigurationFormControls {
  adapterType: FormControl<HardwareAdapterType>;
  configuration: ButlerV1ConfigurationForm;
  validity: FormControl<HardwareConfigurationValidity | null>;
}

export type SupportedHardwareConfigurationForm = FormGroup<SupportedHardwareConfigurationFormControls>;

export interface SupportedHardwareConfigurationRow {
  kind: "supported";
  id: string;
  form: SupportedHardwareConfigurationForm;
}

export interface UnsupportedHardwareConfigurationRow {
  kind: "unsupported";
  id: string;
  adapterType: string;
  payload: OpaqueHardwareConfigurationPayload;
  validity: HardwareAdapterValidity;
}

export type HardwareConfigurationRow = SupportedHardwareConfigurationRow | UnsupportedHardwareConfigurationRow;

export function isButlerV1Configuration(
  configuration: HardwareAdapterConfigurationDto
): configuration is ButlerV1HardwareAdapterConfigurationDto {
  if (configuration.adapterType !== HARDWARE_ADAPTER_TYPES.BUTLER_V1 || !isRecord(configuration.config)) {
    return false;
  }

  const { direction, index } = configuration.config;
  if (isHardwareAdapterOutputOperation(configuration.operation)) {
    return direction === "output" && isHardwareAdapterOutputIndex(index);
  }

  if (isHardwareAdapterFixedInputOperation(configuration.operation)) {
    return direction === "input" && isHardwareAdapterInputIndex(index);
  }

  return (
    configuration.operation === "door.customSignal" &&
    direction === "input" &&
    isHardwareAdapterInputIndex(index) &&
    typeof configuration.config["label"] === "string" &&
    typeof configuration.config["signalId"] === "string"
  );
}

export function createButlerV1ConfigurationForm(
  configuration?: ButlerV1HardwareAdapterConfigurationDto
): SupportedHardwareConfigurationForm {
  const customSignalConfig = configuration?.operation === "door.customSignal" ? configuration.config : undefined;

  return new FormGroup<SupportedHardwareConfigurationFormControls>({
    adapterType: new FormControl(HARDWARE_ADAPTER_TYPES.BUTLER_V1, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    configuration: new FormGroup<ButlerV1ConfigurationFormControls>(
      {
        operation: new FormControl(configuration?.operation ?? null, { validators: [Validators.required] }),
        direction: new FormControl(configuration?.config.direction ?? null, { validators: [Validators.required] }),
        index: new FormControl(configuration?.config.index ?? null, { validators: [Validators.required] }),
        label: new FormControl(customSignalConfig?.label ?? "", {
          nonNullable: true,
        }),
        signalId: new FormControl({ value: customSignalConfig?.signalId ?? null, disabled: true }),
      },
      { validators: [customSignalLabelValidator()] }
    ),
    validity: new FormControl({ value: configuration?.validity ?? { status: "pending" }, disabled: true }),
  });
}

export function toOpaqueHardwareConfigurationPayload(
  configuration: HardwareAdapterConfigurationDto
): OpaqueHardwareConfigurationPayload {
  return {
    operation: configuration.operation,
    adapterType: configuration.adapterType,
    config: configuration.config,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function customSignalLabelValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const operation = control.get("operation")?.value as unknown;
    const label = control.get("label")?.value as unknown;

    if (operation !== "door.customSignal") {
      return null;
    }

    if (typeof label !== "string" || label.trim().length === 0) {
      return { customSignalLabelRequired: true };
    }

    return label.trim().length <= 255 ? null : { customSignalLabelMaxLength: true };
  };
}

function isHardwareAdapterOutputIndex(value: unknown): value is HardwareAdapterOutputIndex {
  return value === 1 || value === 2 || value === 3 || value === 4 || value === 5;
}

function isHardwareAdapterInputIndex(value: unknown): value is 1 | 2 | 3 {
  return value === 1 || value === 2 || value === 3;
}

function isHardwareAdapterOutputOperation(
  value: HardwareAdapterOperation
): value is "door.open" | "door.close" | "door.stop" {
  return value === "door.open" || value === "door.close" || value === "door.stop";
}

function isHardwareAdapterFixedInputOperation(value: HardwareAdapterOperation): value is "door.opened" | "door.closed" {
  return value === "door.opened" || value === "door.closed";
}
